import type { ITransformRT2, IVector3 } from '@seddi/math-v2';
import type { Button, PieceButton, PieceButtonHole } from '@seddi/scene';
import {
  BufferGeometry,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Quaternion,
  Vector3,
} from '@seddi/three';

import type { GarmentGeometryGfx } from '../resources/garmentGeometryTypes';
import type { ResourcesContext } from '../resources/resourcesContext';
import { computeTransformBasis } from './geometryRenderableTransformBasis';
import type { PieceRenderable } from './pieceRenderable';
import {
  type StitchRenderable,
  create as createStitch,
  dispose as disposeStitch,
  updateColor as updateStitchColor,
} from './stitchRenderable';

export type PieceButtonRenderable = {
  garmentGeometryRef?: boolean;
  readonly id: string;
  isOutOfBounds?: boolean;
  readonly mesh: Mesh;
  readonly parent: Object3D;
  resourceRef?: string;
  readonly stitches: StitchRenderable[];
};

/**
 * Create piece button renderable.
 * @param id - Piece button ID.
 * @param parent - Parent node.
 */
export const create = (id: string, parent: Object3D): PieceButtonRenderable => {
  const mesh = new Mesh(geometryPlaceholder, materialPlaceholder);
  mesh.name = `${id}-mesh`;
  parent.add(mesh);
  return { id, mesh, parent, stitches: [] };
};

/**
 * Dispose.
 * @param self - Piece button renderable.
 * @param resources - Resources context.
 */
export const dispose = (self: PieceButtonRenderable, resources: ResourcesContext) => {
  const { mesh, parent } = self;
  detachButton(self, resources);
  parent.remove(mesh);
};

/**
 * Update stitches color.
 * @param self - Piece button renderable.
 * @param color - Stitch color.
 * @param resources - Resources context.
 */
export const updateStitchesColor = (
  self: PieceButtonRenderable,
  color: IVector3,
  resources: ResourcesContext,
) => {
  for (const stitch of self.stitches) {
    updateStitchColor(stitch, color, resources);
  }
};

/**
 * Update transform.
 * @param self - Piece button renderable.
 * @param state - Button or hole state.
 * @param resource - Resource.
 * @param pieceRenderable - Button or hole piece renderable.
 */
export const updateTransformFromPiece = (() => {
  const basis = { normal: new Vector3(), position: new Vector3(), tangent: new Vector3() };
  const bitangent = new Vector3();
  const m = new Matrix4();
  const q = new Quaternion();

  return (
    self: PieceButtonRenderable,
    state: PieceButton | PieceButtonHole,
    buttonState: Button | undefined,
    pieceRenderable: PieceRenderable,
  ) => {
    const { mesh } = self;
    const { transform } = state;

    computeTransformBasis(pieceRenderable, transform, basis);
    const { normal, position, tangent } = basis;
    bitangent.crossVectors(normal, tangent);
    m.makeBasis(tangent, bitangent, normal);
    q.setFromRotationMatrix(m);

    // Button placement depends on button height.
    const hh = buttonState ? buttonState.bounds.height * 0.5 : 0.002;
    position.add(normal.normalize().multiplyScalar(hh));

    mesh.setRotationFromQuaternion(q);
    mesh.position.copy(position);
  };
})();

// ------ BUTTON RESOURCE ------

/**
 * Attach button.
 * @param self - Piece button renderable.
 * @param state - Button state.
 * @param resources - Resources context.
 */
export const attachButton = (
  self: PieceButtonRenderable,
  state: PieceButton,
  resources: ResourcesContext,
) => {
  detachButton(self, resources);

  // Attach to mesh.
  const { mesh } = self;
  const buttonId = state.resourceID;
  const button = resources.getRefButton(buttonId)!;
  mesh.geometry = button.geometry;
  mesh.material = button.material;
  self.resourceRef = buttonId;

  // NOTE: Button placement does not depend on button's resource load, the necessary data is in the descriptor.
};

/**
 * Detach button.
 * @param self - Piece button renderable.
 * @param resources - Resources context.
 */
export const detachButton = (self: PieceButtonRenderable, resources: ResourcesContext) => {
  const { mesh, resourceRef } = self;

  if (!resourceRef) return;

  // Detach from mesh.
  mesh.geometry = geometryPlaceholder;
  mesh.material = materialPlaceholder;
  resources.retRefButton(resourceRef);
  delete self.resourceRef;

  // NOTE: Button placement does not depend on button's resource load, the necessary data is in the descriptor.
};

// ------ GARMENT GEOMETRY RESOURCE ------

/**
 * Attach garment geometry.
 * @param self - Piece button renderable.
 * @param state - Piece button state.
 * @param garmentGeometry - Garment geometry.
 * @param resources - Resources context.
 */
export const attachGarmentGeometry = (
  self: PieceButtonRenderable,
  state: PieceButton,
  garmentGeometry: GarmentGeometryGfx,
  resources: ResourcesContext,
) => {
  detachGarmentGeometry(self);
  // Attach to transform.
  updateTransformGarmentGeometry(self, garmentGeometry);
  // Create stitches.
  createStitchesGarmentGeometry(self, state, garmentGeometry, resources);
  // Update self.
  self.garmentGeometryRef = true;
};

/**
 * Detach garment geometry.
 * @param self - Piece button renderable.
 */
export const detachGarmentGeometry = (self: PieceButtonRenderable) => {
  if (!self.garmentGeometryRef) return;
  // Delete stitches.
  deleteStitchesGarmentGeometry(self);
  // Update self.
  delete self.garmentGeometryRef;
  // NOTE: Button placement after detach depends on the button's position wrt the hole position in 3D space.
  // It is the responsibility of the caller to launch placement functions after all pieces geometry are detached.
};

/**
 * Update transform.
 * @param self - Piece button renderable.
 * @param garmentGeometry - Garment geometry.
 */
const updateTransformGarmentGeometry = (() => {
  const q = new Quaternion();

  return (self: PieceButtonRenderable, garmentGeometry: GarmentGeometryGfx) => {
    const { rotation, translation } = garmentGeometry.buttons.get(self.id)!.transform;
    const { mesh } = self;
    mesh.position.copy(translation);
    mesh.setRotationFromQuaternion(q.copy(rotation));
    mesh.updateMatrix();
  };
})();

/**
 * Create stitches.
 * @param self - Piece button renderable.
 * @param state - Piece button state.
 * @param garmentGeometry - Garment geometry.
 * @param resources - Resources context.
 */
const createStitchesGarmentGeometry = (
  self: PieceButtonRenderable,
  state: PieceButton,
  garmentGeometry: GarmentGeometryGfx,
  resources: ResourcesContext,
) => {
  const { id, mesh, stitches } = self;
  const color = state.recolorThread!;
  const garmentGeometryStitches = garmentGeometry.buttons.get(id)!.stitch;

  for (const { geometry, name } of garmentGeometryStitches) {
    const stitchRenderable = createStitch(mesh.parent, geometry, color, resources);
    stitchRenderable.mesh.name = `${id}-stitch-${name}`;
    stitches.push(stitchRenderable);
  }
};

/**
 * Delete stitches.
 * @param self - Piece button renderable.
 */
const deleteStitchesGarmentGeometry = (self: PieceButtonRenderable) => {
  const { stitches } = self;

  for (const stitchRenderable of stitches) {
    disposeStitch(stitchRenderable);
  }

  stitches.length = 0;
};

// ------ GEOMETRY HELPERS ------

/**
 * Return true if piece and hole are closer than threshold.
 * @param buttonRenderable - Piece button renderable.
 * @param buttonTransform - Button transform.
 * @param holeRenderable - Piece button renderable from hole.
 * @param holeTransform - Hole transform.
 */
export const isCloseToHole = (() => {
  const distanceEps = 0.02; // 2 cm
  const buttonBasis = { normal: new Vector3(), position: new Vector3(), tangent: new Vector3() };
  const holeBasis = { normal: new Vector3(), position: new Vector3(), tangent: new Vector3() };

  return (
    buttonRenderable: PieceRenderable,
    buttonTransform: ITransformRT2,
    holeRenderable: PieceRenderable,
    holeTransform: ITransformRT2,
  ): boolean => {
    computeTransformBasis(buttonRenderable, buttonTransform, buttonBasis);
    computeTransformBasis(holeRenderable, holeTransform, holeBasis);
    return buttonBasis.position.distanceTo(holeBasis.position) <= distanceEps;
  };
})();

// ------ PLACEHOLDERS ------

// Placeholders are defined in global context for now.
const geometryPlaceholder = new BufferGeometry();
const materialPlaceholder = new MeshStandardMaterial();
